import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #defaultCloseDialog = this.closeDialog;
  #drawerObserver;
  #dialog;
  #itemsScrollTop = 0;
  #handleDialogClose = (event) => {
    if (event.target !== this.#dialog) return;
    event.target.removeAttribute('data-cart-refresh');
    this.#itemsScrollTop = 0;
  };

  #rememberItemsScroll = (event) => {
    if (event.target.matches?.('.cart-drawer__items')) {
      this.#itemsScrollTop = event.target.scrollTop;
    }
  };

  #handleDrawerMutations = (records) => {
    // Cart totals, item rows, and payment widgets change frequently. None
    // require modal recovery while the original dialog remains attached.
    if (this.#dialog?.isConnected && this.contains(this.#dialog)
      && !records.some(record => record.type === 'attributes' && record.target === this.#dialog)) return;
    this.#restoreModalAfterRefresh();
  };

  #restoreModalAfterRefresh = () => {
    const dialog = this.#dialog?.isConnected && this.contains(this.#dialog)
      ? this.#dialog
      : this.querySelector('dialog');
    this.#dialog = dialog;
    // A section refresh can copy [open] onto a new dialog without restoring
    // its native top-layer state, leaving it behind the locked page.
    if (!dialog?.open || dialog.matches(':modal') || dialog.classList.contains('dialog-closing')) return;
    const scrollTop = this.#itemsScrollTop;
    // Recovery is an in-place update, not a new drawer entrance.
    dialog.setAttribute('data-cart-refresh', '');
    dialog.removeAttribute('open');
    dialog.showModal();
    const items = dialog.querySelector('.cart-drawer__items');
    if (items) items.scrollTop = scrollTop;
  };

  closeDialog = async () => {
    if (this.hasAttribute('data-package-builder-cart-drawer') && this.refs.dialog.open) {
      window.location.href = this.dataset.packageBuilderRedirectUrl || Theme.routes.root_url || '/';
      return;
    }

    return this.#defaultCloseDialog();
  };

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('scroll', this.#rememberItemsScroll, { capture: true, passive: true });
    this.addEventListener('close', this.#handleDialogClose, true);
    this.#drawerObserver?.disconnect();
    this.#drawerObserver = new MutationObserver(this.#handleDrawerMutations);
    this.#drawerObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });
    this.#restoreModalAfterRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('scroll', this.#rememberItemsScroll, true);
    this.removeEventListener('close', this.#handleDialogClose, true);
    this.#drawerObserver?.disconnect();
    this.#dialog = null;
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open') && !this.querySelector('dialog')?.open) {
      this.showDialog();
    }
  };

  open() {
    if (!this.querySelector('dialog')?.open) this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
